import React, { useState } from "react";
import { Box, TextField, Button, Stack, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Chip } from "@mui/material";
import { Plus, Trash2, UserCheck, UserX } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { assetService } from "../../api/services/assetService";
import { PageHeader, StatusChip, ConfirmDialog } from "../../components/Common";
import DataTable from "../../components/DataTable";

const ASSET_TYPES = ["server", "laptop", "printer", "software", "network_device", "application"];

export default function AssetsPage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignEmployeeId, setAssignEmployeeId] = useState("");
  const [form, setForm] = useState({ asset_type: "laptop", name: "", serial_number: "", warranty_expiry: "" });

  const { data: assets, isLoading } = useQuery({ queryKey: ["assets"], queryFn: () => assetService.list() });
  const { data: expiring } = useQuery({ queryKey: ["assets-expiring"], queryFn: () => assetService.expiringWarranties(30) });

  const createMutation = useMutation({
    mutationFn: () => assetService.create({ ...form, warranty_expiry: form.warranty_expiry || null }),
    onSuccess: () => {
      setCreateOpen(false);
      setForm({ asset_type: "laptop", name: "", serial_number: "", warranty_expiry: "" });
      enqueueSnackbar("Asset created", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["assets"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => assetService.delete(id),
    onSuccess: () => {
      enqueueSnackbar("Asset deleted", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      setDeleteTarget(null);
    },
  });

  const assignMutation = useMutation({
    mutationFn: () => assetService.assign(assignTarget.id, Number(assignEmployeeId)),
    onSuccess: () => {
      enqueueSnackbar("Asset assigned", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      setAssignTarget(null);
      setAssignEmployeeId("");
    },
  });

  const unassignMutation = useMutation({
    mutationFn: (id) => assetService.unassign(id),
    onSuccess: () => {
      enqueueSnackbar("Asset unassigned", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["assets"] });
    },
  });

  const columns = [
    { key: "name", label: "Asset", render: (a) => <strong>{a.name}</strong> },
    { key: "asset_type", label: "Type", render: (a) => <Chip size="small" label={a.asset_type} sx={{ textTransform: "capitalize" }} /> },
    { key: "serial_number", label: "Serial #", render: (a) => a.serial_number || "-" },
    { key: "status", label: "Status", render: (a) => <StatusChip value={a.status} /> },
    { key: "warranty_expiry", label: "Warranty Expiry", render: (a) => a.warranty_expiry || "-" },
    { key: "assigned_to_employee_id", label: "Assigned To", render: (a) => a.assigned_to_employee_id ?? "-" },
    {
      key: "actions", label: "", width: 130,
      render: (a) => (
        <Stack direction="row" spacing={0.5}>
          {a.assigned_to_employee_id ? (
            <Button size="small" onClick={(e) => { e.stopPropagation(); unassignMutation.mutate(a.id); }}><UserX size={15} /></Button>
          ) : (
            <Button size="small" onClick={(e) => { e.stopPropagation(); setAssignTarget(a); }}><UserCheck size={15} /></Button>
          )}
          <Button size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteTarget(a); }}><Trash2 size={15} /></Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Assets"
        subtitle={assets ? `${assets.length} asset(s)` : ""}
        action={<Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>New Asset</Button>}
      />

      {expiring && expiring.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {expiring.length} asset(s) have warranties expiring within 30 days: {expiring.map((a) => a.name).join(", ")}
        </Alert>
      )}

      <DataTable columns={columns} rows={assets} loading={isLoading} emptyMessage="No assets yet" />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>New Asset</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Type" value={form.asset_type} onChange={(e) => setForm({ ...form, asset_type: e.target.value })}>
              {ASSET_TYPES.map((t) => <MenuItem key={t} value={t} sx={{ textTransform: "capitalize" }}>{t.replace("_", " ")}</MenuItem>)}
            </TextField>
            <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth required />
            <TextField label="Serial Number" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} fullWidth />
            <TextField label="Warranty Expiry" type="date" InputLabelProps={{ shrink: true }} value={form.warranty_expiry} onChange={(e) => setForm({ ...form, warranty_expiry: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" disabled={!form.name || createMutation.isPending} onClick={() => createMutation.mutate()}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(assignTarget)} onClose={() => setAssignTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Assign {assignTarget?.name}</DialogTitle>
        <DialogContent>
          <TextField
            label="Employee ID" type="number" fullWidth sx={{ mt: 1 }}
            value={assignEmployeeId} onChange={(e) => setAssignEmployeeId(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAssignTarget(null)} color="inherit">Cancel</Button>
          <Button variant="contained" disabled={!assignEmployeeId || assignMutation.isPending} onClick={() => assignMutation.mutate()}>Assign</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete asset?"
        message={`Delete "${deleteTarget?.name}"? This can't be undone.`}
        confirmLabel="Delete" danger
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
      />
    </Box>
  );
}
